import { Bell, Trash2, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import profile from '/images/ProfileImage.jpg';
import { Breadcrumb } from './ui/navigation/Breadcrumb';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CustomSearchNotifications,
  updateNotification,
} from '@/store/slice/Notifications/notificationSlice';
import type { AppDispatch, RootState } from '@store/store';
import { Button } from './ui/form/Button';
import { useToast } from '@/context/ToastContext';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [notificationModal, setNotificationModal] = useState(false);
  const { data: notifications = [] } = useSelector((state: RootState) => state.notification);
  const userData: any = localStorage.getItem('user');
  const user = JSON.parse(userData || '{}');
  const { showToast } = useToast();
  const unreadCount = notifications?.filter((n: any) => !n.isRead).length || 0;
  const displayUnreadCount = unreadCount > 999 ? '999+' : unreadCount > 99 ? '99+' : unreadCount;

  useEffect(() => {
    dispatch(CustomSearchNotifications({}));
  }, []);

  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const paths = location.pathname.split('/').filter(Boolean);
    return paths.map((path, index) => {
      const label = path.charAt(0).toUpperCase() + path.slice(1);
      const href = '/' + paths.slice(0, index + 1).join('/');
      return index === paths.length - 1 ? { label } : { label, href };
    });
  };

  const handleBreadcrumbClick = (item: BreadcrumbItem) => {
    if (item.href) navigate(item.href);
  };

  const handleViewNotifications = () => {
    setNotificationModal(true);
  };

  const handleMarkAsRead = async (id: any) => {
    try {
      const payload = {
        id,
        isRead: true,
        auditTrailId: id,
      };
      await dispatch(updateNotification(payload)).unwrap();
      showToast({ message: 'Notification Updated Successfully', type: 'success' });
      dispatch(CustomSearchNotifications({ userProfileId: user?.id }));
    } catch (error) {
      console.error('Failed to mark as read:', error);
      showToast({ message: error, type: 'error' });
    }
  };

  const handleDeleteNotification = async (id: any) => {
    try {
      const payload = {
        id,
        isActive: false,
        auditTrailId: id,
      };
      await dispatch(updateNotification(payload)).unwrap();
      showToast({ message: 'Notification Deleted Successfully', type: 'success' });
      dispatch(CustomSearchNotifications({ userProfileId: user?.id }));
    } catch (error) {
      console.error('Failed to mark as read:', error);
      showToast({ message: error, type: 'error' });
    }
  };
  return (
    <>
      {notificationModal && (
        <div className='fixed inset-0' onClick={() => setNotificationModal(false)} />
      )}
      <header className='relative px-4 py-2.5 bg-white flex items-center justify-between shadow-sm border-b border-gray-200'>
        <div className='flex items-center space-x-4'>
          <h1 className='text-sm font-semibold'>
            <Breadcrumb
              items={getBreadcrumbItems()}
              showHomeIcon
              maxItems={5}
              onItemClick={handleBreadcrumbClick}
            />
          </h1>
        </div>
        <div className='flex items-center space-x-4'>
          <div className='flex items-center gap-3 px-3 py-1 rounded-lg hover:bg-gray-100 cursor-pointer'>
            <img
              src={user?.profileImageUrl || profile}
              alt='profile'
              className='w-10 h-10 rounded-full border object-cover'
            />
            <div className='flex flex-col'>
              <span className='text-sm font-medium text-gray-800'>{user?.firstName}</span>
              <span className='text-sm text-gray-500'>{user?.email}</span>
            </div>
          </div>
          <div className='relative'>
            <div className='relative'>
              <motion.button
                onClick={handleViewNotifications}
                className='p-2 rounded-lg hover:bg-gray-100 relative'
              >
                <Bell size={20} />

                {unreadCount > 0 && (
                  <span
                    className='absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center
                    bg-red-500 text-white text-[12px] font-bold rounded-full shadow'
                  >
                    {displayUnreadCount}
                  </span>
                )}
              </motion.button>
            </div>
            {notificationModal && (
              <div className='absolute right-0 top-12 w-[400px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-[999] overflow-hidden'>
                <div className='px-5 py-4 border-b border-gray-100 flex justify-between items-center'>
                  <div className='flex items-center gap-2'>
                    <h2 className='text-sm font-semibold text-gray-900'>Notifications</h2>
                    {notifications?.some((n: any) => !n.isRead) && (
                      <span className='bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full'>
                        {notifications.filter((n: any) => !n.isRead).length}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setNotificationModal(false)}
                    className='p-1 rounded-lg hover:bg-gray-100 text-gray-400'
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className='max-h-[450px] overflow-y-auto divide-y divide-gray-50'>
                  {!notifications?.length ? (
                    <div className='flex flex-col items-center justify-center py-16 gap-2 text-gray-400'>
                      <Bell size={28} />
                      <p className='text-sm'>No notifications</p>
                    </div>
                  ) : (
                    notifications.map((n: any) => {
                      const isUnread = !n.isRead;
                      const initials = n.body
                        ?.split(' ')
                        ?.slice(0, 2)
                        ?.map((w: string) => w[0])
                        ?.join('')
                        ?.toUpperCase();
                      const timeAgo = (() => {
                        const diff = Math.floor((Date.now() - new Date(n.sentOn).getTime()) / 1000);
                        if (diff < 60) return 'just now';
                        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                        return new Date(n.sentOn).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                        });
                      })();
                      return (
                        <div
                          key={n.id}
                          className={`group flex items-start gap-3 px-5 py-4 transition-all cursor-pointer
                          ${isUnread ? 'bg-indigo-50/40 hover:bg-indigo-50' : 'hover:bg-gray-50'}
                          `}
                        >
                          {isUnread && <div className='w-[3px] bg-indigo-500 rounded-full mr-1' />}
                          <div className='relative flex-shrink-0'>
                            {n.profileImageUrl ? (
                              <img
                                src={n.profileImageUrl}
                                className='w-10 h-10 rounded-full object-cover ring-2 ring-white'
                              />
                            ) : (
                              <div className='w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white text-xs font-semibold'>
                                {initials || '?'}
                              </div>
                            )}
                            {isUnread && (
                              <span className='absolute -top-1 -right-1 w-2.5 h-2.5 bg-indigo-500 rounded-full border-2 border-white'></span>
                            )}
                          </div>
                          <div className='flex-1 min-w-0'>
                            <p
                              className={`text-[13px] leading-snug ${
                                isUnread ? 'font-semibold text-gray-900' : 'text-gray-600'
                              }`}
                            >
                              {n.title}
                            </p>

                            <p className='text-xs text-gray-500 mt-0.5 line-clamp-2'>{n.body}</p>

                            <div className='flex items-center justify-between mt-2'>
                              <span className='text-[11px] text-gray-400'>{timeAgo}</span>
                              {isUnread && (
                                <>
                                  <button
                                    className='text-[11px] text-indigo-600 hover:text-indigo-700 font-medium opacity-0 group-hover:opacity-100 transition'
                                    onClick={() => handleMarkAsRead(n.id)}
                                  >
                                    Mark as read
                                  </button>
                                </>
                              )}
                              <Button className='' onClick={() => handleDeleteNotification(n.id)}>
                                <Trash2 className='w-3.5 h-3.5 text-red-500' />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
};
